/**
 * @description
 * Client portal route guard component that ensures only client users
 * can access the portal. Redirects admins to dashboard.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-10
 */

import { Navigate, useLocation } from 'react-router';
import { useAuth } from '../../hooks/useAuth';
import { isDevelopment } from '../../../lib/environment';

interface ClientPortalRouteProps {
  children: React.ReactNode;
}

/**
 * Client portal route guard component
 */
export function ClientPortalRoute({ children }: ClientPortalRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Redirect admins to dashboard
  if (user?.role === 'admin' || user?.isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  // Check if user has client access
  if (!user?.assignedClientId) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="w-full max-w-md space-y-8 rounded-xl border bg-card p-8 shadow-2xl text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
            <svg
              className="h-10 w-10 text-destructive"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground mb-2">
              Access Denied
            </h1>
            <p className="text-muted-foreground mb-4">
              This portal is for Byte clients only. You don't have access to any client data.
            </p>
            <p className="text-sm text-muted-foreground">
              Please contact your administrator if you believe this is an error.
            </p>
          </div>
          <div className="pt-4">
            <button
              onClick={() => window.location.href = '/login'}
              className="w-full rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {children}
      {/* Development mode indicator */}
      {isDevelopment() && (
        <div className="fixed bottom-4 right-4 z-50">
          <div className="rounded-md bg-yellow-100 px-2 py-1 text-xs font-medium text-yellow-800 border border-yellow-200">
            CLIENT PORTAL DEV MODE
          </div>
        </div>
      )}
    </>
  );
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */
